import { FC, useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logoImg from "../assets/2.png";
import { Menu, X, Sun, Moon } from "lucide-react";
import { useLocation } from "react-router-dom";
import FloatingText from '../components/ui/FloatingText';
import Splitting from "splitting";
import "splitting/dist/splitting.css"; // wajib diimport
import { gsap } from 'gsap';

type HoverPopupButtonProps = {
  children: React.ReactNode; // Bisa isi icon atau teks
  popupContent: React.ReactNode;
};

const HoverPopupButton: React.FC<HoverPopupButtonProps> = ({ children, popupContent }) => {
  const revealRef = useRef<HTMLDivElement>(null);
  const shakeTimeline = useRef<gsap.core.Timeline | null>(null);

  const handleMouseEnter = () => {
    gsap.killTweensOf(revealRef.current);
    gsap.set(revealRef.current, { display: 'block' });

    gsap.to(revealRef.current, {
      opacity: 1,
      x: 0,
      duration: 0.1,
      ease: 'power2.out',
      onComplete: () => {
        shakeTimeline.current = gsap.timeline();
        shakeTimeline.current.to(revealRef.current, {
          x: 0,
          y: 20,
          duration: 0.5,
          repeat: 10,
          rotation: -20,
          yoyo: true,
          ease: 'power1.inOut',
        });
      },
    });
  };

  const handleMouseLeave = () => {
    shakeTimeline.current?.kill();
    shakeTimeline.current = null;

    gsap.to(revealRef.current, {
      opacity: 0,
      x: 20,
      duration: 0.4,
      ease: 'power3.in',
      delay: 0.3,
      onComplete: () => {
        gsap.set(revealRef.current, { display: 'none' });
      },
    });
  };

  useEffect(() => {
    gsap.set(revealRef.current, { opacity: 0, x: 20, display: 'none' });
  }, []);

  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="bg-black dark:bg-white text-white dark:text-black text-sm font-medium px-4 py-1.5 rounded-full hover:opacity-80 transition"
      >
        {children}
      </button>

      <div
        ref={revealRef}
        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-4 py-2 bg-primary/90 dark:bg-neutral-900 text-white dark:text-white rounded-lg shadow-lg z-10 w-max"
      >
        {popupContent}
      </div>
    </div>
  );
};


const Navbar: FC = () => {
  // const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const location = useLocation();
  const locationNav = useLocation();

  const navItems = [
    { label: "Beranda", path: "/home" },
    { label: "Tentang", path: "/about" },
    { label: "Layanan", path: "/services" },
    { label: "Kontak", path: "/contact" },
  ];

  const [activeSection, setActiveSection] = useState("/");
  const [underlineProps, setUnderlineProps] = useState<{ left: number; top?: number; width: number }>({ left: 0, width: 0 });
  const desktopItemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const mobileItemRefs = useRef<(HTMLLIElement | null)[]>([]);
  
  // Scroll Effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);

      const sectionOffsets = navItems.map((item) => {
        const id = item.path.replace("/", "") || "home";
        const el = document.getElementById(id);
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        return {
          id: item.path,
          top: rect.top,
        };
      });

      const visible = sectionOffsets
        .filter((s) => s && s.top < window.innerHeight * 0.3)
        .sort((a, b) => (b?.top || 0) - (a?.top || 0))[0];

      if (visible && visible.id !== activeSection) {
        setActiveSection(visible.id);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeSection]);

  useEffect(() => {
    const currentRefs = open ? mobileItemRefs.current : desktopItemRefs.current;
    const index = navItems.findIndex((item) => item.path === activeSection);
    const el = currentRefs[index];
    if (el) {
      const { offsetLeft, offsetTop, clientWidth } = el;
      setUnderlineProps({
        left: open ? 0 : offsetLeft,
        top: open ? offsetTop : undefined,
        width: clientWidth,
      });
    }
  }, [activeSection, open]);
  // end

  // Dark mode toggle
  useEffect(() => {
    const stored = localStorage.getItem("theme");
    if (stored === "dark") {
      setDarkMode(true);
      document.documentElement.classList.add("dark");
    }
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle("dark");
    localStorage.setItem("theme", darkMode ? "light" : "dark");
  };

  useEffect(() => {
    Splitting();
  }, []);

  return (
    <nav 
    onMouseEnter={() => setHovered(true)}
    onMouseLeave={() => setHovered(false)}
    className={`w-full sticky top-0 z-50 transition-all duration-500 
    ${scrolled ? "top-0 bg-transparent dark:bg-surface/10 backdrop-blur-lg" : "bg-transparent top-4 transition-all duration-0"}
    `}>
      <div className={`
        absolute left-1/2 -translate-x-1/2 top-0 md:top-0
        inline-flex items-center justify-between
        px-6 sm:px-10 md:px-12
        py-2 sm:py-3 md:py-4
        border border-black dark:border-white
        rounded-full bg-white/80 dark:bg-black/60 backdrop-blur-md shadow-xl
        transition-all duration-300
        w-[90%] sm:w-[70%] md:w-[80%] lg:w-[50%] xl:w-[50%]
        z-50
      `}>

        {/* Logo */}
        <div className="flex items-center gap-2 mr-4">
          <div className="w-10 h-10 rounded-full overflow-hidden">
            <img src={logoImg} alt="logo" className="w-full h-full object-cover" />
          </div>
        
          {/* <FloatingText */}
              <h2 
                data-splitting
                className={`
                  font-semibold text-lg text-primary dark:text-primary md:text-sm lg:text-lg
                  ${hovered ? "headline headline--flip transition-all duration-300" : ""}
                `}>
                Genius Akademi
              </h2>
        </div>

        {/* Desktop Menu */}
        <ul className="relative hidden md:flex items-center gap-6 font-medium text-sm text-black dark:text-white">
          {navItems.map((item, i) => (
            <li
              key={i}
              ref={(el) => {
                desktopItemRefs.current[i] = el;
              }}
              className="relative px-1 cursor-pointer hover:text-primary dark:hover:text-primary transition"
            >
              <a href={`#${item.path.replace("/", "")}`} className="block">
                {item.label}
              </a>
            </li>
          ))}

          {/* Underline */}
          <motion.div
            layout
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
            className="absolute -bottom-1 h-[2px] bg-primary/80 dark:bg-primary/70"
            style={{
              left: underlineProps.left,
              width: underlineProps.width,
            }}
          />
        </ul>

        {/* Desktop Buttons */}
        <div className="hidden md:flex items-center gap-4">
          <button
            onClick={toggleDarkMode}
            className="text-black dark:text-white hover:text-primary dark:hover:text-primary/80 transition bg-surface/10 dark:bg-white/10 rounded-full p-1"
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          {/* <button className="bg-black dark:bg-white text-white dark:text-black text-sm font-medium px-4 py-1.5 rounded-full hover:opacity-80 transition">
            Hubungi
          </button> */}
          <HoverPopupButton
            popupContent={
              <div className="flex items-center gap-3">
                <img src="/logo-head-genius.png" alt="Kontak" className="w-10 h-10 rounded-full" />
                <span className="text-sm">contact@geniusakademi.com</span>
              </div>
            }
          >
            Hubungi
          </HoverPopupButton>

        </div>

        {/* Mobile Toggle */}
        <div className="md:hidden flex items-center gap-2">
          <button onClick={toggleDarkMode} className="text-black dark:text-white">
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <button onClick={() => setOpen(!open)}>
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -300 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="md:hidden w-[90%] max-w-6xl mx-auto mt-2 px-6 pb-4 rounded-[8vw] bg-white/90 dark:bg-black/80 backdrop-blur-md shadow-lg border border-black dark:border-white"
          >
            <ul className="flex flex-col gap-3 font-medium text-sm text-black dark:text-white mt-20">
                {navItems.map((item, i) => (
                    <li
                      key={i}
                      ref={(el) => {
                        mobileItemRefs.current[i] = el;
                      }}
                      className="relative px-1 cursor-pointer hover:underline"
                    >
                      <a href={`#${item.path.replace("/", "")}`} onClick={() => setOpen(false)}>
                        {item.label}
                      </a>
                    </li>
                  ))}
              <li>
                {/* Underline Mobile */}
                {open && (
                  <motion.div
                    layout
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    className="absolute h-[2px] bg-primary/80 dark:bg-primary/70"
                    style={{
                      // top: underlineProps.top,
                      top: (underlineProps.top ?? 0) + 24, // tambahkan 4px ke bawah
                      left: 25,
                      width: underlineProps.width,
                    }}
                  />
                )}

                <button className="w-full bg-black dark:bg-white text-white dark:text-black text-sm font-medium px-4 py-2 mt-2 rounded-full hover:opacity-80 transition">
                  Hubungi
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

{/* Mobile Menu */}


    </nav>
  );
};

export default Navbar;
